/**
 * <es-recommendations> — a Yespo (eSputnik) recommendation block
 * (sections/esputnik-recommendations.liquid).
 *
 *   1. Waits for the Yespo app embed's window.eS, then calls
 *      eS('getRecommendations', { variantId, productId | categoryKey | productIds }).
 *   2. The response carries product_id, container_type and url only, so each
 *      card is the theme's own, fetched from
 *      /products/<handle>?section_id=esputnik-recommendation-card.
 *   3. Every product link in a card is pointed at the returned url exactly as
 *      Yespo sent it — it carries sc_content, which is how clicks are counted.
 *   4. The filled block is marked up with the recommendation contract
 *      (data-es-recommendations / data-es-recommendation-product /
 *      data-es-container-type) and handed to assets/esputnik-events.js, which
 *      sends one ProductImpressions per block when it scrolls into view.
 *
 * Until then the section is hidden; a block Yespo answers empty never shows.
 */
if (!customElements.get('es-recommendations')) {
  const CARD_SECTION = 'esputnik-recommendation-card';
  const GIVE_UP_MS = 30000;
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const debugging = () => {
    try {
      return !!window.localStorage.getItem('esTrackingDebug');
    } catch (error) {
      return false;
    }
  };

  /** Resolves with window.eS once the Yespo embed has defined it, or null. */
  const whenYespoReady = () =>
    new Promise((resolve) => {
      if (typeof window.eS === 'function') return resolve(window.eS);
      const started = Date.now();
      const timer = setInterval(() => {
        if (typeof window.eS === 'function') {
          clearInterval(timer);
          resolve(window.eS);
        } else if (Date.now() - started > GIVE_UP_MS) {
          clearInterval(timer);
          resolve(null);
        }
      }, 250);
    });

  /** handle + variant -> Promise<card HTML | null>, shared by every block on the page. */
  const cardCache = new Map();

  const loadCard = (handle, variantId) => {
    const key = `${handle}:${variantId || ''}`;
    if (!cardCache.has(key)) {
      const params = new URLSearchParams({ section_id: CARD_SECTION });
      if (variantId) params.set('variant', variantId);
      cardCache.set(
        key,
        fetch(`${root}products/${encodeURIComponent(handle)}?${params}`, { credentials: 'same-origin' })
          .then((response) => (response.ok ? response.text() : null))
          .then((html) => {
            if (!html) return null;
            const card = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-es-card]');
            return card ? card.innerHTML : null;
          })
          .catch(() => null)
      );
    }
    return cardCache.get(key);
  };

  /** The product handle and variant a recommendation url points at. */
  const parseUrl = (url) => {
    try {
      const parsed = new URL(url, window.location.origin);
      const match = parsed.pathname.match(/\/products\/([^/?#]+)/);
      if (!match) return null;
      const variant = parsed.searchParams.get('variant');
      return {
        handle: decodeURIComponent(match[1]),
        variantId: variant && /^\d+$/.test(variant) ? variant : null,
      };
    } catch (error) {
      return null;
    }
  };

  class EsRecommendations extends HTMLElement {
    /** `localStorage.esTrackingDebug = 1` logs every step, prefixed with the Yespo variant ID. */
    log(...args) {
      if (debugging()) console.log(`[esRecommendations ${this.dataset.variantId || '?'}]`, ...args);
    }

    connectedCallback() {
      if (this.loaded) return;
      this.loaded = true;
      this.load();
    }

    params() {
      const { variantId, request, productId, categoryKey } = this.dataset;
      if (!variantId) return null;

      const params = { variantId };
      if (request === 'product') {
        // The PDP's live key: esputnik-events.js keeps it on the selected variant.
        const primary = document.querySelector('[data-es-product][data-es-primary]');
        const key = ((primary && primary.dataset.esProductKey) || productId || '').trim();
        if (!key) return null;
        params.productId = key;
      } else if (request === 'category') {
        if (!categoryKey) return null;
        params.categoryKey = categoryKey;
      } else if (request === 'cart_products') {
        let ids = [];
        try {
          ids = JSON.parse(this.dataset.productIds || '[]');
        } catch (error) {
          ids = [];
        }
        params.productIds = ids.map((id) => String(id).trim()).filter(Boolean);
      }
      return params;
    }

    async load() {
      const params = this.params();
      if (!params) {
        this.log('1. no request sent — missing variant ID or product/category data', { ...this.dataset });
        return;
      }
      this.log('1. request', params);

      const eS = await whenYespoReady();
      if (!eS) {
        this.log('2. window.eS never appeared (Yespo embed not loaded)');
        return;
      }

      const products = await new Promise((resolve) => {
        try {
          eS('getRecommendations', params, (error, result) => {
            if (error) {
              this.log('2. Yespo error', error);
              resolve([]);
            } else {
              resolve(Array.isArray(result) ? result : []);
            }
          });
        } catch (error) {
          this.log('2. eS threw', error);
          resolve([]);
        }
      });
      this.log(`2. Yespo returned ${products.length} item(s)`, products);

      const limit = parseInt(this.dataset.limit, 10) || products.length;
      const usable = products.filter((item) => item && item.product_id && item.url && parseUrl(item.url));
      if (usable.length < products.length) {
        this.log(`3. dropped ${products.length - usable.length} item(s) without product_id or a /products/ url`);
      }
      const cards = await Promise.all(
        usable.map((item) => {
          const { handle, variantId } = parseUrl(item.url);
          return loadCard(handle, variantId).then((html) => {
            if (!html) this.log('4. no card for', handle, variantId);
            return html ? { item, html } : null;
          });
        })
      );

      const hideSoldOut = this.dataset.hideSoldOut === 'true';
      const shown = cards
        .filter(Boolean)
        .filter(({ html }) => !(hideSoldOut && html.includes('card-wrapper--sold-out')))
        .slice(0, limit);

      this.log(`5. ${cards.filter(Boolean).length} card(s) loaded, ${shown.length} shown (sold-out hidden: ${hideSoldOut})`);
      if (shown.length) {
        this.render(shown);
        this.log('6. rendered');
      }
    }

    render(shown) {
      const template = this.querySelector('template[data-es-shell]');
      if (!template) return;

      // Cloned from the template's inert document, so slider-component and
      // collection-tabs upgrade only once the slides are in place.
      const shell = template.content.cloneNode(true);
      const block = shell.querySelector('.collection');
      const list = shell.querySelector('[data-es-slot]');
      const sectionId = this.dataset.sectionId;

      shown.forEach(({ item, html }, index) => {
        const slide = document.createElement('li');
        slide.id = `Slide-${sectionId}-${index + 1}`;
        slide.className = 'grid__item slider__slide';
        slide.dataset.esRecommendationProduct = String(item.product_id);
        slide.dataset.esContainerType = String(item.container_type || '');
        slide.innerHTML = html;

        // Yespo's url, unchanged — it carries the sc_content click tracking.
        slide.querySelectorAll('a[href*="/products/"]').forEach((link) => {
          link.href = item.url;
        });
        list.appendChild(slide);
      });

      if (shown.length < 2) {
        list.classList.remove('slider', 'slider--everywhere');
        shell.querySelector('[data-es-slider-buttons]')?.remove();
      }

      block.setAttribute('data-es-recommendations', '');
      block.dataset.esContainerType = String(shown[0].item.container_type || '');

      this.querySelector('[data-es-placeholder]')?.remove();
      template.after(shell);
      this.classList.remove('hidden');

      if (window.esTracking && typeof window.esTracking.trackRecommendations === 'function') {
        window.esTracking.trackRecommendations(block);
      }
    }
  }

  customElements.define('es-recommendations', EsRecommendations);
}
